import { NavPage } from '../../../components/page/Breadcrumbs';
import { OneDriveUploadSession } from '../../../services/upload/OneDriveUploadProvider';
import {
  useRetryUploadSessions,
  useUpload,
  useUploadFiles,
  useUploadSessions
} from '../../../services/upload/UploadProvider';
import { FC, useState } from 'react';
import strings from '../documents.translations';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer } from '../../../components/page/PageSizer';
import { useIntroduction } from '../../../services/graph/hooks/introduction';
import { Item } from './Item';
import { SectionHeader } from '../../../components/page/SectionHeader';
import { FaBoxOpen } from 'react-icons/fa';
import { FiUpload, FiX } from 'react-icons/fi';
import { DropBox } from '../../../components/file/DropBox';
import { Button } from '../../../components/button/Button';
import { RetryModal } from './RetryModal';
import { UploadStatus } from '../../../services/upload/utils';
import { CollapsibleSection } from '../../../components/page/CollapsibleSection';
import { FiCheck } from 'react-icons/fi';
import { useCustomerId } from '../../../services/authentication/hooks/user';

export const UploadFiles: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: strings.common.pages.documents,
      to: '/documents'
    },
    {
      name: strings.uploadFile,
      to: '/documents/upload'
    }
  ];

  const introduction = useIntroduction();

  if (!introduction.data) {
    throw Error('Could not get image data');
  }

  const sessions = useUploadSessions<OneDriveUploadSession>();

  const completedSessions = sessions.filter(session => session.status === UploadStatus.Complete);
  const cancelledSessions = sessions.filter(session => session.status === UploadStatus.Cancelled);
  const failedSessions = sessions.filter(session => session.status === UploadStatus.Failed);

  const pendingSessions = sessions.filter(
    session =>
      session.status !== UploadStatus.Complete &&
      session.status !== UploadStatus.Cancelled &&
      session.status !== UploadStatus.Failed
  );

  const error = useUpload(state => state.uploadError);

  const uploadFiles = useUploadFiles();
  const retryUploadSessions = useRetryUploadSessions();

  const [filesToUpload, setFilesToUpload] = useState<FileList | undefined>();
  const [bulkRetryModalOpen, setBulkRetryModalOpen] = useState<boolean>(false);
  const [openCompletedDefault, setOpenCompletedDefault] = useState<boolean>(false);

  const customerDriveId: string = useCustomerId();

  return (
    <PageContainer>
      <PageHeader
        title={strings.uploadFile}
        breadcrumbs={breadcrumbs}
        imageUrl={introduction.data.backgroundImages.documentsPage}
      ></PageHeader>
      <PageSizer className="py-5 flex flex-col gap-10">
        <SectionHeader title={strings.upload.newFiles} Icon={FaBoxOpen}></SectionHeader>
        <DropBox
          files={filesToUpload}
          error={error}
          onChange={(newFileList?: FileList) => {
            setFilesToUpload(newFileList);
          }}
          onUpload={async (fileList: FileList) => {
            setOpenCompletedDefault(true);
            try {
              await uploadFiles(fileList, customerDriveId);
              setFilesToUpload(undefined);
            } catch (e) {
              console.log(e);
            }
          }}
        />
        {pendingSessions.length > 0 && (
          <>
            <div className="flex justify-between items-center">
              <SectionHeader title={strings.upload.inProgress} Icon={FiUpload}></SectionHeader>
              <Button
                primary
                onClick={() => {
                  setBulkRetryModalOpen(true);
                }}
              >
                {strings.upload.bulkRetry}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pendingSessions.map(session => (
                <div key={session.id}>
                  <Item id={session.id} />
                </div>
              ))}
            </div>
          </>
        )}
        {completedSessions.length > 0 && (
          <CollapsibleSection
            header={strings.upload.completed(completedSessions.length)}
            defaultExpanded={openCompletedDefault}
            Icon={FiCheck}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {completedSessions.map(session => (
                  <div key={session.id}>
                    <Item id={session.id} />
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        )}
        {cancelledSessions.length > 0 && (
          <CollapsibleSection
            header={strings.upload.cancelled(cancelledSessions.length)}
            defaultExpanded={openCompletedDefault}
            Icon={FiX}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cancelledSessions.map(session => (
                  <div key={session.id}>
                    <Item id={session.id} />
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        )}
        {failedSessions.length > 0 && (
          <CollapsibleSection
            header={strings.upload.failed(cancelledSessions.length)}
            defaultExpanded={openCompletedDefault}
            Icon={FiX}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cancelledSessions.map(session => (
                  <div key={session.id}>
                    <Item id={session.id} />
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        )}
      </PageSizer>
      <RetryModal
        description="Select a set of files, and any uploads for those files will be resumed or retried."
        open={bulkRetryModalOpen}
        onSubmit={async (files: FileList) => {
          retryUploadSessions(files);
        }}
        onClose={() => setBulkRetryModalOpen(false)}
      />
    </PageContainer>
  );
};
